/* eslint-disable react/no-array-index-key */
/* eslint-disable no-nested-ternary */
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Dropdown from 'rc-dropdown';
import Menu, { Item as MenuItem } from 'rc-menu';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import styled from '@emotion/styled';
import IcomoonReact from 'icomoon-react';
import PropTypes from 'prop-types';
import 'rc-dropdown/assets/index.css';
import { useTheme } from 'emotion-theming';
import Text from '../../../../shared/components/Text/Text';
import iconSet from '../../../../shared/images/teambuildr-selection.json';
import { setIsStaticAddColumnDropdownMenuOpen, setActiveModal } from '../../ducks/calendarActions';
import { claimArrowKeys, hasArrowKeyOwner } from '../../utils/arrowKeyOwnership';
import { FOCUS_FILL_VAR } from '../../../../shared/theme/focusStyles';

const DropdownIcon = styled('div')`
  padding: 4px;
  background: ${({ theme }) => theme.colors.buttonPrimaryBg};
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
`;

const DropdownContainerStatic = styled('div')`
  display: flex;
  align-items: center;
  cursor: pointer;
`;

/** Focusable wrapper for a custom `trigger` (transposed saved-edit
 * footer Add Property button) — keyboard-accessible with a light-blue
 * focus background, matching AddPropertyColumnComponent. */
const TriggerWrapper = styled('div')`
  display: inline-flex;
  border-radius: 4px;
  &:focus { outline: none; }
  &:focus-visible {
    outline: none;
    background: ${FOCUS_FILL_VAR};
  }
`;

const MenuWrapper = styled('div')`
  display: flex;
  overflow: hidden;
  flex-direction: column;

`;

const CustomDivider = styled.div`
  height: 1px;
  margin: 4px 4;
  background-color: ${({ theme }) => theme.colors.borderLight};
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  border-radius: 4px;
  width: 100%;
`;

const DropdownHeader = styled('div')`
  background-color: ${({ theme }) => theme.colors.surface};
  padding-bottom: 8px;
  display: flex;
  justify-content: space-between;
  padding: 7px;
`;

const ArrowDiv = styled('div')`
  display: flex;
  justify-content: space-between;
  width: 100%;
  align-items: center;
`;

const MenuText = styled('div')`
  font-family: 'Nunito Sans';
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 0px;
  font-size: 16px;
`;

const AddPropContainer = styled('div')`
  font-family: 'Nunito Sans';
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 0px;
  font-size: 16px;
`;

// Create a new HoverableMenuItem component similar to CalendarItemDropdownOptions
const HoverableMenuItem = ({ item, onClick, isFocused }) => {
  const [isHovered, setIsHovered] = useState(false);
  const isHighlighted = isHovered || isFocused;

  const { colors } = useTheme();
  return (
    <MenuItem
      style={{
        paddingTop: 7,
        paddingBottom: 7,
        paddingLeft: 14,
        paddingRight: 7,
        cursor: 'pointer',
        background: isHighlighted ? colors.surfaceSecondary : 'transparent',
      }}
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <IconContainer>
        {item.icon ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <IcomoonReact
              iconSet={iconSet}
              size={18}
              icon={item.icon}
              color={colors.textSecondary}
              style={{ marginRight: 5 }}
            />
            <MenuText>{item.label}</MenuText>
          </div>
        ) : (
          <ArrowDiv>
            <MenuText>{item.label}</MenuText>
          </ArrowDiv>
        )}
      </IconContainer>
    </MenuItem>
  );
};

HoverableMenuItem.propTypes = {
  item: PropTypes.shape({
    label: PropTypes.string.isRequired,
    icon: PropTypes.string,
  }).isRequired,
  onClick: PropTypes.func.isRequired,
  isFocused: PropTypes.bool,
};

HoverableMenuItem.defaultProps = {
  isFocused: false,
};

const capWords = (str) => str
  .split(' ')
  .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
  .join(' ');

const StaticAddColumnDropdownContainer = ({
  size,
  primaryRows,
  primaryRowSwitch,
  setPrimaryRowSwitch,
  setIntensityUnit,
  setRestPerRepUnit,
  setPrimaryRowSwitchState,
  containerRef,
  assignedId,
  trigger,
  closeOnSelect,
  onPropertyAdded,
}) => {
  const dispatch = useDispatch();
  const { colors } = useTheme();
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const activeExerciseId = useSelector((state) => state.calendar.data.activeExerciseId);

  // Filter to get only visible (not already selected) items
  const visibleItems = primaryRows.filter((row) => !primaryRowSwitch[row.name]);

  // Keyboard shortcut: Ctrl+P / Cmd+P to open dropdown
  // Only responds when the parent exercise is the active exercise
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'p') {
        /** preventDefault stays ahead of every bail so the browser's native
         * Print dialog never appears while a calendar row is mounted. */
        e.preventDefault();
        /** Second route into the same double-navigation: opening this on top
         * of the options menu leaves both arrowing off one keypress. */
        if (hasArrowKeyOwner()) return;
        if (assignedId && assignedId !== activeExerciseId) return;
        setDropdownVisible(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeExerciseId, assignedId]);

  // Arrow key navigation when dropdown is open
  useEffect(() => {
    if (!dropdownVisible) {
      return undefined;
    }

    const handleKeyDown = (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        // +1 for "About Properties" item at the end
        setFocusedIndex((prev) => Math.min(prev + 1, visibleItems.length));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusedIndex((prev) => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter' && focusedIndex >= 0) {
        e.preventDefault();
        if (focusedIndex < visibleItems.length) {
          // Click on a property item
          const row = visibleItems[focusedIndex];
          const flippedStatus = !primaryRowSwitch[row.name];
          const newSwitch = { ...primaryRowSwitch };
          newSwitch[row.name] = flippedStatus;

          const intensityMatch = row.name.match(/^intensity \(([^)]+)\)$/);
          if (intensityMatch && setIntensityUnit) {
            setIntensityUnit(intensityMatch[1]);
          }
          const restPerRepMatch = row.name.match(/^rest per rep \(([^)]+)\)$/);
          if (restPerRepMatch && setRestPerRepUnit) {
            setRestPerRepUnit(restPerRepMatch[1]);
          }
          if (!flippedStatus) {
            setPrimaryRowSwitch(newSwitch);
          } else if (row.name === 'peak power' || row.name === 'velocity') {
            setPrimaryRowSwitch(newSwitch);
          } else {
            setPrimaryRowSwitchState(newSwitch);
          }
          if (flippedStatus && onPropertyAdded) onPropertyAdded(row.name);
        } else {
          // Click on "About Properties"
          dispatch(setActiveModal('aboutExercisePropertiesModal'));
        }
        setDropdownVisible(false);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setDropdownVisible(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    dropdownVisible,
    focusedIndex,
    visibleItems,
    primaryRowSwitch,
    dispatch,
    setIntensityUnit,
    setRestPerRepUnit,
    setPrimaryRowSwitch,
    setPrimaryRowSwitchState,
    onPropertyAdded,
  ]);

  // Reset focused index when dropdown opens/closes
  useEffect(() => {
    if (dropdownVisible) {
      setFocusedIndex(0);
    } else {
      setFocusedIndex(-1);
    }
  }, [dropdownVisible]);

  /** Own the arrow keys while open, so useCalendarKeyboardScroll leaves the
   * calendar where it is instead of sliding a row out from under the menu. */
  useEffect(() => {
    if (!dropdownVisible) return undefined;
    return claimArrowKeys();
  }, [dropdownVisible]);

  const handleMenuItemClick = (row, e) => {
    e.domEvent.stopPropagation();
    const flippedStatus = !primaryRowSwitch[row.name];
    const newSwitch = { ...primaryRowSwitch };
    newSwitch[row.name] = flippedStatus;

    const intensityMatch = row.name.match(/^intensity \(([^)]+)\)$/);
    if (intensityMatch && setIntensityUnit) {
      setIntensityUnit(intensityMatch[1]);
    }
    const restPerRepMatch = row.name.match(/^rest per rep \(([^)]+)\)$/);
    if (restPerRepMatch && setRestPerRepUnit) {
      setRestPerRepUnit(restPerRepMatch[1]);
    }
    if (!flippedStatus) {
      setPrimaryRowSwitch(newSwitch);
    } else if (row.name === 'peak power' || row.name === 'velocity') {
      setPrimaryRowSwitch(newSwitch);
    } else {
      setPrimaryRowSwitchState(newSwitch);
    }
    if (flippedStatus && onPropertyAdded) onPropertyAdded(row.name);
    if (closeOnSelect) {
      setDropdownVisible(false);
    }
  };

  const handleAboutPropertiesClick = (e) => {
    // e.domEvent.stopPropagation();
    dispatch(setActiveModal('aboutExercisePropertiesModal'));
  };

  const menu = (
    <Menu
      style={{
        width: 280,
        minHeight: 100,
        overflowY: 'auto', /** Scroll when the content exceeds the height */
        marginBottom: '0px',
        border: `1px solid ${colors.borderLight}`,
        backgroundColor: colors.surfaceElevated,
      }}
    >
      <AddPropContainer onClick={(e) => e.stopPropagation()}>
        <DropdownHeader>
          <Text fontWeight={700} fontSize='16px'>
            Add Property
          </Text>
          <Text fontSize='16px' color={colors.textSecondary}>
            Ctrl+P
          </Text>

        </DropdownHeader>
      </AddPropContainer>
      <MenuWrapper style={{ gap: 4 }}>
        {visibleItems.map((primaryRow, index) => (
          <HoverableMenuItem
            key={`first-${primaryRow.name}-${index}`}
            item={{
              label: primaryRow.name === '%pr'
                ? '%PR'
                : capWords(primaryRow.name.includes('intensity')
                  ? 'Intensity'
                  : primaryRow.name.includes('rest per rep')
                    ? 'Rest Per Rep'
                    : primaryRow.name),
            }}
            onClick={(e) => handleMenuItemClick(primaryRow, e)}
            isFocused={focusedIndex === index}
          />
        ))}
        <CustomDivider />
        <HoverableMenuItem
          key='about-properties'
          item={{
            label: 'About Properties',
            icon: 'info2',
          }}
          onClick={handleAboutPropertiesClick}
          isFocused={focusedIndex === visibleItems.length}
        />
      </MenuWrapper>
    </Menu>
  );

  return (
    <Dropdown
      trigger={['click']}
      overlay={menu}
      aria-haspopup='true'
      visible={dropdownVisible}
      onVisibleChange={(visible) => {
        setDropdownVisible(visible);
        dispatch(setIsStaticAddColumnDropdownMenuOpen(visible));
      }}
    >
      {trigger ? (
        <TriggerWrapper
          role='button'
          tabIndex={0}
          aria-haspopup='listbox'
          aria-expanded={dropdownVisible}
          aria-label='Add property'
          onClick={(e) => {
            /** Let rc-dropdown handle the click-to-toggle (matching the
             * default trigger); just stop the click from bubbling. */
            e.stopPropagation();
          }}
          onKeyDown={(e) => {
            /** An open popup owns the keyboard. The options menu is
             * highlight-only, so focus can still be sitting on this trigger
             * while the coach navigates it — without this bail, ArrowDown
             * opens this dropdown too and both menus then arrow in lockstep
             * off a single keypress. */
            if (hasArrowKeyOwner()) return;
            /** Enter / Space / ArrowDown open the dropdown. stopPropagation
             * keeps the opening keystroke from reaching the global
             * keydown nav listener (which would immediately select the
             * first property). */
            if (!dropdownVisible && (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown')) {
              e.preventDefault();
              e.stopPropagation();
              e.nativeEvent.stopImmediatePropagation();
              setDropdownVisible(true);
            }
          }}
        >
          {trigger}
        </TriggerWrapper>
      ) : (
        <DropdownContainerStatic
          onClick={(e) => {
            e.stopPropagation();
          }}
          tabIndex={-1}
        >
          <DropdownIcon padding={size} data-tooltip-id={`dropdownIcon-${assignedId}`}>
            <IcomoonReact
              iconSet={iconSet}
              size={15}
              icon='plus'
              color={colors.white}
            />
          </DropdownIcon>
          <ReactTooltip
            id={`dropdownIcon-${assignedId}`}
            key={`dropdownIcon-${assignedId}`}
            place='top'
            content='Add Property'
            style={{
              zIndex: 9999,
              backgroundColor: 'rgba(68, 68, 68, 1)',
              color: colors.white,
              fontFamily: 'Nunito Sans',
              fontSize: '13px',
            }}
          />
        </DropdownContainerStatic>
      )}
    </Dropdown>
  );
};

StaticAddColumnDropdownContainer.propTypes = {
  size: PropTypes.number.isRequired,
  primaryRows: PropTypes.instanceOf(Array).isRequired,
  primaryRowSwitch: PropTypes.instanceOf(Object).isRequired,
  setPrimaryRowSwitch: PropTypes.func.isRequired,
  setIntensityUnit: PropTypes.func.isRequired,
  setRestPerRepUnit: PropTypes.func,
  setPrimaryRowSwitchState: PropTypes.func.isRequired,
  containerRef: PropTypes.shape({
    current: PropTypes.instanceOf(Element),
  }),
  assignedId: PropTypes.string,
  trigger: PropTypes.node,
  closeOnSelect: PropTypes.bool,
  onPropertyAdded: PropTypes.func,
};

StaticAddColumnDropdownContainer.defaultProps = {
  setRestPerRepUnit: null,
  containerRef: null,
  assignedId: null,
  trigger: null,
  closeOnSelect: false,
  onPropertyAdded: null,
};

export default StaticAddColumnDropdownContainer;
